import { useState } from "react";
import { Plus, FileSpreadsheet, FileDown, TrendingUp, TrendingDown, Scale, HandCoins } from "lucide-react";
import { toast } from "sonner";
import { api, useApi, downloadFile, errMsg } from "../lib/api";
import { PageHead, Card, Field, Input, Btn, Loading, Overline } from "../components/kit";
import { ExpenseDialog } from "../components/finance/ExpenseDialog";
import { MonthlyChart } from "../components/finance/MonthlyChart";
import { IncomeTable, ReceivableTable, ExpenseTable } from "../components/finance/FinanceTables";
import { todayWIB, rupiah, num } from "../lib/constants";

const TABS = [["income", "Pemasukan"], ["receivable", "Piutang"], ["expense", "Pengeluaran"]];

function Stat({ icon: Icon, label, value, tone, testid }) {
  return (
    <Card className="!p-4 flex items-start gap-3" data-testid={testid}>
      <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${tone}`}><Icon className="w-5 h-5" /></div>
      <div className="min-w-0"><Overline>{label}</Overline><div className="mt-1 font-mono font-extrabold text-lg truncate">{value}</div></div>
    </Card>
  );
}

function SummaryCards({ s }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="fin-summary">
      <Stat icon={TrendingUp} label="Pemasukan" value={rupiah(s.total_income)} tone="bg-emerald-50 text-emerald-600" testid="fin-total-income" />
      <Stat icon={TrendingDown} label="Pengeluaran" value={rupiah(s.total_expense)} tone="bg-rose-50 text-rose-600" testid="fin-total-expense" />
      <Stat icon={Scale} label="Laba Bersih" value={rupiah(s.net)} tone={s.net >= 0 ? "bg-sky-50 text-sky-600" : "bg-rose-50 text-rose-600"} testid="fin-net" />
      <Stat icon={HandCoins} label={`Piutang (${s.receivable_count} order)`} value={rupiah(s.total_receivable)} tone="bg-amber-50 text-amber-600" testid="fin-total-receivable" />
    </div>
  );
}

function MethodRecap({ rows }) {
  if (!rows.length) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="fin-method-recap">
      {rows.map((m) => (
        <Card key={m.method} className="!p-4 space-y-1" data-testid={`fin-method-${m.method}`}>
          <Overline>{m.method}</Overline>
          <div className="flex justify-between text-sm"><span className="text-slate-500">Masuk</span><span className="font-mono font-bold text-emerald-700">{rupiah(m.income)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-slate-500">Keluar</span><span className="font-mono font-bold text-rose-600">{rupiah(m.expense)}</span></div>
          <div className="flex justify-between text-sm border-t pt-1"><span className="text-slate-500">Selisih</span><span className={`font-mono font-extrabold ${m.net >= 0 ? "text-sky-700" : "text-rose-600"}`}>{rupiah(m.net)}</span></div>
        </Card>
      ))}
    </div>
  );
}

function RangeBar({ range, setRange, onExport, busy }) {
  return (
    <Card className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
      <Field label="Dari"><Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="fin-start-input" /></Field>
      <Field label="Sampai"><Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="fin-end-input" /></Field>
      <Btn variant="success" loading={busy === "xlsx"} onClick={() => onExport("xlsx")} data-testid="fin-export-excel-btn"><FileSpreadsheet className="w-5 h-5" /> Excel</Btn>
      <Btn variant="danger" loading={busy === "pdf"} onClick={() => onExport("pdf")} data-testid="fin-export-pdf-btn"><FileDown className="w-5 h-5" /> PDF</Btn>
    </Card>
  );
}

function Tabs({ tab, setTab }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1" data-testid="fin-tabs">
      {TABS.map(([k, l]) => (
        <button key={k} onClick={() => setTab(k)} data-testid={`fin-tab-${k}`}
          className={`shrink-0 h-11 px-4 rounded-full font-bold text-sm border ${tab === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300"}`}>{l}</button>
      ))}
    </div>
  );
}

const TAB_VIEW = {
  income: (s) => <IncomeTable rows={s.income} />,
  receivable: (s) => <ReceivableTable rows={s.receivables} />,
  expense: (s, h) => <ExpenseTable rows={s.expenses} byCategory={s.expense_by_category} onEdit={h.onEdit} onDelete={h.onDelete} />,
};

export default function Finance() {
  const today = todayWIB();
  const [tab, setTab] = useState("income");
  const [range, setRange] = useState({ start: today.slice(0, 8) + "01", end: today });
  const [busy, setBusy] = useState("");
  const [edit, setEdit] = useState(null);
  const qs = `start=${range.start}&end=${range.end}`;
  const { data: s, mutate } = useApi(`/finance/summary?${qs}`);
  const exp = async (fmt) => {
    setBusy(fmt);
    try { await downloadFile(`/finance/export?${qs}&format=${fmt}`, `keuangan_${range.start}_${range.end}.${fmt}`); } catch (x) { toast.error(errMsg(x)); } finally { setBusy(""); }
  };
  const del = async (e) => {
    if (!window.confirm(`Hapus pengeluaran "${e.description}"?`)) return;
    try { await api.delete(`/finance/expenses/${e.id}`); toast.success("Pengeluaran dihapus"); mutate(); } catch (x) { toast.error(errMsg(x)); }
  };
  const newExpense = () => setEdit({ date: today, category: "Pembelian Supplier", supplier: "", description: "", amount: "", note: "" });
  return (
    <>
      <PageHead title="Keuangan" sub="Pemasukan, piutang & pengeluaran" action={
        <Btn onClick={newExpense} data-testid="add-expense-btn"><Plus className="w-5 h-5" /> <span className="hidden sm:inline">Pengeluaran</span></Btn>} />
      <RangeBar range={range} setRange={setRange} onExport={exp} busy={busy} />
      {!s && <Loading />}
      {s && (
        <>
          <SummaryCards s={s} />
          <MonthlyChart />
          <MethodRecap rows={s.by_method} />
          <Tabs tab={tab} setTab={setTab} />
          {TAB_VIEW[tab](s, { onEdit: setEdit, onDelete: del })}
          <p className="text-xs text-slate-500">Nilai order pada periode: <b className="font-mono">{rupiah(s.order_value)}</b> dari {num(s.order_count)} order.</p>
        </>
      )}
      {edit && <ExpenseDialog e={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); mutate(); }} />}
    </>
  );
}
